import { useEffect, useRef, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { COMMON_EMOJIS, RichTextEditor } from '@/components/RichTextEditor';
import { normalizeSafeUrl, sanitizeHtml } from '@/lib/sanitizeHtml';
import StorefrontSection from '@/components/StorefrontSection';
import { getSectionStylesForBreakpoint } from '@/lib/responsiveStyles';
import {
  createSiteTemplatePage,
  DEFAULT_SITE_DESIGN_NAMES,
} from '@/lib/siteDesigns';
import {
  LayoutTemplate, Layers, Users, Settings, Search, Eye, Pencil, ChevronDown, ChevronUp,
  RotateCcw, Upload, Plus, GripVertical, CheckCircle, Trash2, EyeOff, MoveDiagonal, Globe, Package, Save,
} from 'lucide-react';
import {
  DEFAULT_SHARED_STOREFRONT_CONTENT,
  normalizeSharedStorefrontContent,
  readSharedStorefrontContent,
  SHARED_STOREFRONT_CONTENT_KEY,
} from '@/lib/whiteLabelSharedContent';


import {
  INITIAL_PARTNERS,
  INITIAL_SECTIONS,
  STARTER_SECTION_OPTIONS,
  readStoredAdminSettings,
  readBuilderVisibilitySettings,
  readBuilderVisibility,
  optimizeUploadedCardImage,
  createAdminTemplatePages,
  readAdminTemplatePages,
} from '@/lib/whiteLabelManagerLib';
import { SettingToggle } from '@/components/builder/SettingToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWhiteLabelManager() {
  const initialSearch = new URLSearchParams(window.location.search);
  const initialPageParam = Number(initialSearch.get('page'));
  const initialPage = Number.isInteger(initialPageParam) && initialPageParam >= 0
    ? initialPageParam
    : 0;
  const VALID_TABS = ['library', 'builder', 'all-sites', 'content', 'pages', 'settings'];
  const [activeTab, setActiveTab] = useState(() => {
    const requested = initialSearch.get('tab');
    return VALID_TABS.includes(requested) ? requested : 'library';
  });
  const [editingVariantsType, setEditingVariantsType] = useState(null);
  const [expandedVariantsType, setExpandedVariantsType] = useState(null);
  const [addingSection, setAddingSection] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sections, setSections] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library')) || [];
      return INITIAL_SECTIONS.map(initial => {
        const configured = stored.find(item => item.type === initial.type);
        if (!configured) return { ...initial };
        const configuredVariants = Array.isArray(configured.variants) && typeof configured.variants[0] === 'string'
          ? [...new Set([...configured.variants, ...initial.variants.slice(-2)])]
          : initial.variants;
        return {
          ...initial,
          ...configured,
          variants: configuredVariants,
          disabledVariants: Array.isArray(configured.disabledVariants) ? configured.disabledVariants : []
        };
      });
    } catch {
      return INITIAL_SECTIONS;
    }
  });
  const [partners, setPartners] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cloudsetu_partner_pages'));
      return Array.isArray(stored) && stored.length ? stored : INITIAL_PARTNERS;
    } catch {
      return INITIAL_PARTNERS;
    }
  });
  const [allowCustomCode, setAllowCustomCode] = useState(false);
  const [allowCustomDomain, setAllowCustomDomain] = useState(true);
  const [badgeMode, setBadgeMode] = useState('Optional');
  const [starterSectionTypes, setStarterSectionTypes] = useState(
    STARTER_SECTION_OPTIONS.map(([type]) => type).filter(type => type !== 'testimonials')
  );
  const [sharedContent, setSharedContent] = useState(readSharedStorefrontContent);
  const [showRightEditorInBuilder, setShowRightEditorInBuilder] = useState(readBuilderVisibility);
  const [settingsSaveStatus, setSettingsSaveStatus] = useState('');
  const [librarySaveStatus, setLibrarySaveStatus] = useState('');
  const [adminTemplatePage, setAdminTemplatePage] = useState(initialPage);
  const [adminTemplatePages, setAdminTemplatePages] = useState(
    () => readAdminTemplatePages(readSharedStorefrontContent(), sections)
  );
  const [adminSelectedSectionId, setAdminSelectedSectionId] = useState(
    () => readAdminTemplatePages(readSharedStorefrontContent(), sections)[initialPage]?.sections?.[0]?.id || null
  );
  const [adminSelectedElement, setAdminSelectedElement] = useState('section');
  const [adminPageHistory, setAdminPageHistory] = useState({ past: [], future: [] });
  const [adminPreviewWidth, setAdminPreviewWidth] = useState('desktop');
  const [adminLibraryPanelWidth, setAdminLibraryPanelWidth] = useState(250);
  const [adminPropertiesPanelWidth, setAdminPropertiesPanelWidth] = useState(420);
  const [adminCardEmojiPickerOpen, setAdminCardEmojiPickerOpen] = useState(false);
  const [cardImageUploadError, setCardImageUploadError] = useState('');
  const [adminBuilderSaving, setAdminBuilderSaving] = useState(false);
  const adminPanelResizeRef = useRef(null);
  const adminResizeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // On mount: sync section library AND page builder from backend (backend = source of truth)
    const syncFromBackend = async () => {
      try {
        // 1. Fetch section library from backend
        const libResp = await axios.get(`${API}/admin/white-label/section-library`);
        const backendLib = libResp.data?.sections;
        if (Array.isArray(backendLib) && backendLib.length) {
          const libKey = 'cloudsetu_admin_section_library';
          const existingLib = JSON.parse(localStorage.getItem(libKey) || 'null');
          const libUpdatedAt = libResp.data?.updatedAt;
          const localUpdatedAt = existingLib?._updatedAt;
          if (!existingLib || (libUpdatedAt && (!localUpdatedAt || libUpdatedAt > localUpdatedAt))) {
            localStorage.setItem(libKey, JSON.stringify(backendLib.map(s => ({ ...s, _updatedAt: libUpdatedAt }))));
            setSections(backendLib);
          }
        }
      } catch { /* silent */ }

      try {
        // 2. Fetch page builder designs from backend
        const { data } = await axios.get(`${API}/admin/white-label/page-builder`);
        if (data?.pages && Object.keys(data.pages).length) {
          const builderKey = 'cloudsetu_admin_page_builder';
          const existing = JSON.parse(localStorage.getItem(builderKey) || 'null');
          if (!existing || (data.updatedAt && (!existing.updatedAt || data.updatedAt > existing.updatedAt))) {
            localStorage.setItem(builderKey, JSON.stringify(data));
            const freshPages = readAdminTemplatePages(readSharedStorefrontContent(), sections);
            setAdminTemplatePages(freshPages);
          }
        }
      } catch { /* silent */ }
    };
    syncFromBackend();

    try {
      if (localStorage.getItem('cloudsetu_admin_section_library') === null) {
        localStorage.setItem('cloudsetu_admin_section_library', JSON.stringify(sections));
      }
      const builderKey = 'cloudsetu_admin_page_builder';
      const savedBuilder = JSON.parse(localStorage.getItem(builderKey) || 'null');
      const savedLibrary = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || '[]');
      if (Array.isArray(savedLibrary) && !savedLibrary.some(section => section.type === 'header')) {
        const header = sections.find(section => section.type === 'header');
        if (header) localStorage.setItem('cloudsetu_admin_section_library', JSON.stringify([...savedLibrary, header]));
      }
      // NOTE: Do NOT write adminTemplatePages here — syncFromBackend() handles it.
      // Writing large page data here is the primary cause of localStorage QuotaExceeded.
      window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
    } catch (error) {
      console.error('Unable to initialize Admin-owned builder data.', error);
      setSettingsSaveStatus('Could not initialize the section library and templates in this browser.');
    }
  }, []);

  const tabs = [
    { key: 'library', label: 'Sections Library', icon: Layers },
    { key: 'builder', label: 'Page Builder', icon: LayoutTemplate },
    { key: 'all-sites', label: 'All Sites', icon: Globe },
    { key: 'content', label: 'Storefront Content', icon: Pencil },
    { key: 'pages', label: 'Partner Pages', icon: Users },
    { key: 'settings', label: 'Global Settings', icon: Settings },
  ];

  const persistSections = (nextSections) => {
    const pages = Object.fromEntries(Object.entries(adminTemplatePages).map(([pageIndex, page]) => [pageIndex, {
        ...page,
        sections: (page.sections || []).map(builderSection => {
          const librarySection = nextSections.find(item => item.type === builderSection.type);
          if (!librarySection) return builderSection;
          return {
            ...builderSection,
            name: librarySection.name || builderSection.name,
          };
        }),
    }]));

    const libraryKey = 'cloudsetu_admin_section_library';
    const builderKey = 'cloudsetu_admin_page_builder';
    let previousLibrary;
    let previousBuilder;
    let writesStarted = false;
    try {
      previousLibrary = localStorage.getItem(libraryKey);
      previousBuilder = localStorage.getItem(builderKey);
      writesStarted = true;
      localStorage.setItem(libraryKey, JSON.stringify(nextSections));
      localStorage.setItem(builderKey, JSON.stringify({ pages, activeTemplatePage: adminTemplatePage }));
    } catch (error) {
      if (writesStarted) {
        try {
          if (previousLibrary === null) localStorage.removeItem(libraryKey);
          else localStorage.setItem(libraryKey, previousLibrary);
          if (previousBuilder === null) localStorage.removeItem(builderKey);
          else localStorage.setItem(builderKey, previousBuilder);
        } catch (rollbackError) {
          console.error('Unable to restore Admin library data after a failed save.', rollbackError);
        }
      }
      console.error('Unable to save Admin section availability.', error);
      setLibrarySaveStatus(`Could not save section access: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }

    setSections(nextSections);
    setAdminTemplatePages(pages);
    setLibrarySaveStatus('Section access saved. Admin page layouts are preserved.');
    window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
    // Background sync section library to backend (source of truth)
    axios.put(`${API}/admin/white-label/section-library`, { sections: nextSections }).catch(() => {});
    return true;
  };

  const persistSharedContent = (nextContent) => {
    const normalized = normalizeSharedStorefrontContent(nextContent);
    setSharedContent(normalized);
    localStorage.setItem(SHARED_STOREFRONT_CONTENT_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new Event('cloudsetu-shared-content-updated'));
    setAdminTemplatePages(previous => {
      const pages = Object.fromEntries(Object.entries(previous).map(([pageIndex, page]) => [pageIndex, {
        ...page,
        sections: (page.sections || []).map(section => {
          const { contentOverrides, ...layout } = section;
          return {
            ...layout,
            heading: normalized[section.type]?.heading ?? '',
            subtext: normalized[section.type]?.subtext ?? '',
            primaryText: normalized[section.type]?.primaryText ?? '',
            secondaryText: normalized[section.type]?.secondaryText ?? '',
            primaryUrl: normalized[section.type]?.primaryUrl ?? '',
            secondaryUrl: normalized[section.type]?.secondaryUrl ?? '',
            kicker: normalized[section.type]?.kicker ?? '',
            mediaUrl: normalized[section.type]?.mediaUrl ?? '',
            mediaAlt: normalized[section.type]?.mediaAlt ?? '',
            cards: normalized[section.type]?.cards ?? [],
          };
        }),
      }]));
      try { localStorage.setItem('cloudsetu_admin_page_builder', JSON.stringify({ pages, activeTemplatePage: adminTemplatePage })); } catch {}
      return pages;
    });
  };

  const persistAdminBuilderPages = (pages) => {
    setAdminPageHistory(history => ({
      past: [...history.past, adminTemplatePages].slice(-50),
      future: [],
    }));
    setAdminTemplatePages(pages);
    const builderData = { pages, activeTemplatePage: adminTemplatePage, updatedAt: new Date().toISOString() };
    // section library is stored separately — do NOT embed it here (causes QuotaExceededError)
    try {
      localStorage.setItem('cloudsetu_admin_page_builder', JSON.stringify(builderData));
    } catch (storageError) {
      console.warn('localStorage quota reached for page builder. Backend is source of truth.', storageError);
    }
    // Background sync to backend
    axios.put(`${API}/admin/white-label/page-builder`, builderData).catch(() => {});
    window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
  };

  const restoreAdminBuilderPages = pages => {
    setAdminTemplatePages(pages);
    try { localStorage.setItem('cloudsetu_admin_page_builder', JSON.stringify({ pages, activeTemplatePage: adminTemplatePage })); } catch {}
    const restoredSections = pages[adminTemplatePage]?.sections || [];
    if (!restoredSections.some(section => section.id === adminSelectedSectionId)) {
      setAdminSelectedSectionId(restoredSections[0]?.id || null);
      setAdminSelectedElement('section');
    }
  };

  const undoAdminBuilderChange = () => {
    if (!adminPageHistory.past.length) return;
    const previous = adminPageHistory.past[adminPageHistory.past.length - 1];
    setAdminPageHistory({
      past: adminPageHistory.past.slice(0, -1),
      future: [adminTemplatePages, ...adminPageHistory.future].slice(0, 50),
    });
    restoreAdminBuilderPages(previous);
  };

  const redoAdminBuilderChange = () => {
    if (!adminPageHistory.future.length) return;
    const next = adminPageHistory.future[0];
    setAdminPageHistory({
      past: [...adminPageHistory.past, adminTemplatePages].slice(-50),
      future: adminPageHistory.future.slice(1),
    });
    restoreAdminBuilderPages(next);
  };

  const updateAdminBuilderContent = (sectionId, field, value) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    persistSharedContent({
      ...sharedContent,
      [section.type]: { ...sharedContent[section.type], [field]: value },
    });
  };

  const updateAdminBuilderCard = (sectionId, cardIndex, field, value) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    const cards = [...(section.contentOverrides?.cards || section.cards || sharedContent[section.type]?.cards || [])];
    if (!cards[cardIndex]) return;
    cards[cardIndex] = { ...cards[cardIndex], [field]: value };
    updateAdminBuilderContent(sectionId, 'cards', cards);
  };

  const handleAdminBuilderCardImageUpload = async (sectionId, cardIndex, file) => {
    if (!file) return;
    setCardImageUploadError('');
    try {
      const image = await optimizeUploadedCardImage(file);
      updateAdminBuilderCard(sectionId, cardIndex, 'image', image);
    } catch (error) {
      console.error('Unable to upload card image.', error);
      setCardImageUploadError(error instanceof Error ? error.message : 'Unable to save this image.');
    }
  };

  const moveAdminBuilderBlock = (sectionId, blockId, direction) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    const blocks = [...(section.blocks || [])];
    const index = blocks.findIndex(block => block.id === blockId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= blocks.length) return;
    [blocks[index], blocks[nextIndex]] = [blocks[nextIndex], blocks[index]];
    updateAdminBuilderSection(sectionId, { blocks });
  };

  const reorderAdminBuilderBlocks = (sectionId, sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    const blocks = [...(section.blocks || [])];
    const sourceIndex = blocks.findIndex(block => block.id === sourceId);
    const targetIndex = blocks.findIndex(block => block.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const [moved] = blocks.splice(sourceIndex, 1);
    blocks.splice(targetIndex, 0, moved);
    updateAdminBuilderSection(sectionId, { blocks });
  };

  const addAdminNestedBlock = (sectionId, blockId, type) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    const block = section?.blocks?.find(item => item.id === blockId);
    if (!section || !block || block.type !== 'container') return;
    const children = [...(block.children || []), {
      id: `admin-nested-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      text: type === 'heading' ? 'Nested heading' : 'Nested text',
    }];
    updateAdminBuilderBlock(sectionId, blockId, { children });
  };

  const updateAdminNestedBlock = (sectionId, blockId, childId, text) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    const block = section?.blocks?.find(item => item.id === blockId);
    if (!block) return;
    updateAdminBuilderBlock(sectionId, blockId, {
      children: (block.children || []).map(child => child.id === childId ? { ...child, text } : child),
    });
  };

  const duplicateAdminBuilderBlock = (sectionId, blockId) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    const index = section?.blocks?.findIndex(block => block.id === blockId) ?? -1;
    if (!section || index < 0) return;
    const original = section.blocks[index];
    const duplicate = { ...original, id: `admin-block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, title: `${original.title} copy` };
    const blocks = [...section.blocks];
    blocks.splice(index + 1, 0, duplicate);
    updateAdminBuilderSection(sectionId, { blocks });
    setAdminSelectedElement(`block:${duplicate.id}`);
  };

  const updateAdminBuilderSection = (sectionId, updates) => {
    const pages = {
      ...adminTemplatePages,
      [adminTemplatePage]: {
        ...(adminTemplatePages[adminTemplatePage] || {}),
        sections: (adminTemplatePages[adminTemplatePage]?.sections || []).map(section =>
          section.id === sectionId ? { ...section, ...updates } : section
        ),
      },
    };
    persistAdminBuilderPages(pages);
  };

  const changeAdminBuilderPage = (pageIndex) => {
    if (!adminTemplatePages[pageIndex]) return;
    setAdminTemplatePage(pageIndex);
    setAdminSelectedSectionId(adminTemplatePages[pageIndex]?.sections?.[0]?.id || null);
    setAdminSelectedElement('section');
    try { localStorage.setItem('cloudsetu_admin_page_builder', JSON.stringify({ pages: adminTemplatePages, activeTemplatePage: pageIndex })); } catch {}
  };

  const addAdminTemplatePage = () => {
    const indices = Object.keys(adminTemplatePages).map(Number).filter(Number.isInteger);
    const pageIndex = indices.length ? Math.max(...indices) + 1 : 0;
    const source = adminTemplatePages[adminTemplatePage] || Object.values(adminTemplatePages)[0];
    const name = `Site ${pageIndex + 1}`;
    const sectionsForNewPage = (source?.sections || []).map((section, order) => {
      const { contentOverrides, ...layout } = section;
      return {
        ...layout,
        id: `admin-sec-${section.type}-page-${pageIndex + 1}-${order + 1}`,
        order,
      };
    });
    const pages = {
      ...adminTemplatePages,
      [pageIndex]: { name, sections: sectionsForNewPage },
    };
    setAdminTemplatePage(pageIndex);
    setAdminSelectedSectionId(sectionsForNewPage[0]?.id || null);
    setAdminSelectedElement('section');
    persistAdminBuilderPages(pages);
    window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
  };

  const renameAdminTemplatePage = (pageIndex, name) => {
    const nextName = name.trim();
    if (!nextName || nextName === adminTemplatePages[pageIndex]?.name) return;
    const pages = {
      ...adminTemplatePages,
      [pageIndex]: { ...adminTemplatePages[pageIndex], name: nextName },
    };
    persistAdminBuilderPages(pages);
    window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
  };

  const deleteAdminTemplatePage = (targetIndex) => {
    const deleteIndex = Number.isInteger(targetIndex) ? targetIndex : adminTemplatePage;
    const indices = Object.keys(adminTemplatePages).map(Number).filter(Number.isInteger);
    if (indices.length <= 1) {
      setSettingsSaveStatus('At least one Admin design must remain available.');
      return;
    }
    const remaining = indices.filter(index => index !== deleteIndex);
    const nextPage = remaining[0];
    const pages = { ...adminTemplatePages };
    delete pages[deleteIndex];
    setAdminTemplatePage(nextPage);
    setAdminSelectedSectionId(pages[nextPage]?.sections?.[0]?.id || null);
    setAdminSelectedElement('section');
    persistAdminBuilderPages(pages);
    window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
  };

  const saveAdminBuilderToBackend = async () => {
    setAdminBuilderSaving(true);
    try {
      const builderData = {
        pages: adminTemplatePages,
        activeTemplatePage: adminTemplatePage,
        updatedAt: new Date().toISOString(),
      };
      await axios.put(`${API}/admin/white-label/page-builder`, builderData);
      try {
        localStorage.setItem('cloudsetu_admin_page_builder', JSON.stringify(builderData));
      } catch {}
      window.dispatchEvent(new Event('cloudsetu-admin-library-updated'));
      setSettingsSaveStatus('Design saved successfully.');
      setTimeout(() => setSettingsSaveStatus(''), 3000);
    } catch (error) {
      setSettingsSaveStatus('Could not save design. Please try again.');
    } finally {
      setAdminBuilderSaving(false);
    }
  };

  const moveAdminBuilderSection = (sectionId, direction) => {
    const currentSections = [...(adminTemplatePages[adminTemplatePage]?.sections || [])];
    const index = currentSections.findIndex(section => section.id === sectionId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= currentSections.length) return;
    [currentSections[index], currentSections[nextIndex]] = [currentSections[nextIndex], currentSections[index]];
    persistAdminBuilderPages({
      ...adminTemplatePages,
      [adminTemplatePage]: { ...adminTemplatePages[adminTemplatePage], sections: currentSections },
    });
  };

  const addAdminBuilderSection = (section, variant) => {
    const currentPageSections = adminTemplatePages[adminTemplatePage]?.sections || [];
    const content = sharedContent[section.type] || DEFAULT_SHARED_STOREFRONT_CONTENT[section.type] || {};
    const newSection = {
      id: `admin-sec-${section.type}-${Date.now()}`,
      type: section.type,
      name: section.name,
      variant: variant || section.variants?.[0] || section.variants,
      visible: true,
      heading: content.heading ?? '',
      subtext: content.subtext ?? '',
      kicker: content.kicker ?? '',
      primaryText: content.primaryText ?? '',
      cards: content.cards ?? [],
      blocks: [],
      styles: {},
    };
    persistAdminBuilderPages({
      ...adminTemplatePages,
      [adminTemplatePage]: {
        ...(adminTemplatePages[adminTemplatePage] || {}),
        sections: [...currentPageSections, newSection],
      },
    });
    setAdminSelectedSectionId(newSection.id);
    setAdminSelectedElement('section');
  };

  const addAdminBuilderBlock = (sectionId, type) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    const block = {
      id: `admin-block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      title: type === 'heading' ? 'New heading' : type === 'text' ? 'Write your text here' : type === 'button' ? 'Learn more' : type === 'card' ? 'New card' : type === 'shape' ? 'Shape' : 'Container content',
      text: type === 'card' || type === 'container' ? 'Add supporting content here.' : '',
      ...(type === 'button' ? { href: '#contact' } : {}),
    };
    updateAdminBuilderSection(sectionId, { blocks: [...(section.blocks || []), block] });
    setAdminSelectedElement(`block:${block.id}`);
  };

  const updateAdminBuilderBlock = (sectionId, blockId, updates) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    updateAdminBuilderSection(sectionId, {
      blocks: (section.blocks || []).map(block => block.id === blockId ? { ...block, ...updates } : block),
    });
  };

  const removeAdminBuilderBlock = (sectionId, blockId) => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    updateAdminBuilderSection(sectionId, { blocks: (section.blocks || []).filter(block => block.id !== blockId) });
    setAdminSelectedElement('section');
  };

  const updateAdminBuilderStyle = (sectionId, element, property, value, breakpoint = 'desktop') => {
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section) return;
    if (breakpoint === 'desktop') {
      updateAdminBuilderSection(sectionId, {
        styles: {
          ...(section.styles || {}),
          [element]: {
            ...(section.styles?.[element] || {}),
            [property]: value,
          },
        },
      });
      return;
    }

    updateAdminBuilderSection(sectionId, {
      responsiveStyles: {
        ...(section.responsiveStyles || {}),
        [breakpoint]: {
          ...(section.responsiveStyles?.[breakpoint] || {}),
          [element]: {
            ...(section.responsiveStyles?.[breakpoint]?.[element] || {}),
            [property]: value,
          },
        },
      },
    });
  };

  const resetAdminBuilderBreakpointStyles = (sectionId, breakpoint) => {
    if (breakpoint === 'desktop') return;
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === sectionId);
    if (!section?.responsiveStyles?.[breakpoint]) return;
    const responsiveStyles = { ...section.responsiveStyles };
    delete responsiveStyles[breakpoint];
    updateAdminBuilderSection(sectionId, { responsiveStyles });
  };

  const beginAdminPanelResize = (event, panel, inputType = 'pointer') => {
    event.preventDefault();
    event.stopPropagation();
    adminPanelResizeRef.current = {
      pointerId: event.pointerId,
      inputType,
      panel,
      startX: event.clientX,
      libraryWidth: adminLibraryPanelWidth,
      propertiesWidth: adminPropertiesPanelWidth,
    };
  };

  const moveAdminPanelResize = event => {
    const resize = adminPanelResizeRef.current;
    if (!resize || resize.inputType !== 'pointer' || resize.pointerId !== event.pointerId) return;
    const delta = event.clientX - resize.startX;
    if (resize.panel === 'library') {
      setAdminLibraryPanelWidth(Math.min(420, Math.max(180, resize.libraryWidth + delta)));
      return;
    }
    setAdminPropertiesPanelWidth(Math.min(520, Math.max(320, resize.propertiesWidth - delta)));
  };

  const endAdminPanelResize = event => {
    if (adminPanelResizeRef.current?.inputType === 'pointer' && adminPanelResizeRef.current.pointerId === event.pointerId) {
      adminPanelResizeRef.current = null;
    }
  };

  const moveAdminPanelMouseResize = event => {
    const resize = adminPanelResizeRef.current;
    if (!resize || resize.inputType !== 'mouse') return;
    const delta = event.clientX - resize.startX;
    if (resize.panel === 'library') {
      setAdminLibraryPanelWidth(Math.min(420, Math.max(180, resize.libraryWidth + delta)));
      return;
    }
    setAdminPropertiesPanelWidth(Math.min(520, Math.max(320, resize.propertiesWidth - delta)));
  };

  const endAdminPanelMouseResize = () => {
    if (adminPanelResizeRef.current?.inputType === 'mouse') {
      adminPanelResizeRef.current = null;
    }
  };

  useEffect(() => {
    window.addEventListener('pointermove', moveAdminPanelResize);
    window.addEventListener('pointerup', endAdminPanelResize);
    window.addEventListener('pointercancel', endAdminPanelResize);
    window.addEventListener('mousemove', moveAdminPanelMouseResize);
    window.addEventListener('mouseup', endAdminPanelMouseResize);
    return () => {
      window.removeEventListener('pointermove', moveAdminPanelResize);
      window.removeEventListener('pointerup', endAdminPanelResize);
      window.removeEventListener('pointercancel', endAdminPanelResize);
      window.removeEventListener('mousemove', moveAdminPanelMouseResize);
      window.removeEventListener('mouseup', endAdminPanelMouseResize);
    };
  }, []);

  const beginAdminSectionResize = (event, section) => {
    event.preventDefault();
    event.stopPropagation();
    const canvasSection = event.currentTarget.closest('[data-testid^="admin-canvas-section-"]');
    adminResizeRef.current = {
      sectionId: section.id,
      startX: event.clientX,
      startY: event.clientY,
      width: canvasSection?.getBoundingClientRect().width || 0,
      height: canvasSection?.getBoundingClientRect().height || 0,
      deltaX: 0,
      deltaY: 0,
    };
  };

  const finishAdminSectionResize = () => {
    const resize = adminResizeRef.current;
    if (!resize) return;
    adminResizeRef.current = null;
    const section = (adminTemplatePages[adminTemplatePage]?.sections || []).find(item => item.id === resize.sectionId);
    const styles = section?.styles || {};
    updateAdminBuilderSection(resize.sectionId, {
      styles: {
        ...styles,
        section: {
          ...(styles.section || {}),
          width: `${Math.max(180, resize.width + resize.deltaX)}px`,
          minHeight: `${Math.max(80, resize.height + resize.deltaY)}px`,
        },
      },
    });
  };

  useEffect(() => {
    const handlePointerMove = event => {
      if (!adminResizeRef.current) return;
      adminResizeRef.current.deltaX = event.clientX - adminResizeRef.current.startX;
      adminResizeRef.current.deltaY = event.clientY - adminResizeRef.current.startY;
    };
    const handlePointerUp = () => finishAdminSectionResize();
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [adminTemplatePages, adminTemplatePage]);

  const addAdminBuilderCard = (section) => {
    const cards = [...(section.contentOverrides?.cards || section.cards || sharedContent[section.type]?.cards || [])];
    const sample = cards[0] || { title: '', text: '' };
    const card = Object.fromEntries(Object.entries(sample).map(([field, value]) => [
      field,
      Array.isArray(value) ? [] : typeof value === 'boolean' ? false : '',
    ]));
    if ('title' in sample || !Object.keys(sample).length) card.title = 'New card';
    if ('text' in sample || !Object.keys(sample).length) card.text = 'Add card details';
    updateAdminBuilderContent(section.id, 'cards', [...cards, card]);
  };

  const removeAdminBuilderCard = (section, cardIndex) => {
    const cards = [...(section.contentOverrides?.cards || section.cards || sharedContent[section.type]?.cards || [])];
    cards.splice(cardIndex, 1);
    updateAdminBuilderContent(section.id, 'cards', cards);
    setAdminSelectedElement('section');
  };

  const updateSharedSection = (type, updates) => {
    persistSharedContent({
      ...sharedContent,
      [type]: { ...sharedContent[type], ...updates },
    });
  };

  const updateSharedCard = (type, cardIndex, field, value) => {
    const cards = [...sharedContent[type].cards];
    cards[cardIndex] = { ...cards[cardIndex], [field]: value };
    updateSharedSection(type, { cards });
  };

  const addSharedCard = (type) => {
    const sample = sharedContent[type].cards[0] || { title: '', text: '' };
    const newCard = Object.fromEntries(Object.entries(sample).map(([field, value]) => [
      field,
      Array.isArray(value) ? [] : typeof value === 'boolean' ? false : '',
    ]));
    updateSharedSection(type, { cards: [...sharedContent[type].cards, newCard] });
  };

  useEffect(() => {
    const refreshAdminContent = (event) => {
      if (event.key && ![
        SHARED_STOREFRONT_CONTENT_KEY,
        'cloudsetu_admin_settings',
        'cloudsetu_admin_global_settings',
      ].includes(event.key)) return;
      if (!event.key || event.key === SHARED_STOREFRONT_CONTENT_KEY) {
        const nextContent = readSharedStorefrontContent();
        setSharedContent(nextContent);
        setAdminTemplatePages(previous => Object.fromEntries(Object.entries(previous).map(([pageIndex, page]) => [pageIndex, {
          ...page,
          sections: (page.sections || []).map(section => ({
            ...section,
            heading: nextContent[section.type]?.heading ?? section.heading,
            subtext: nextContent[section.type]?.subtext ?? section.subtext,
            primaryText: nextContent[section.type]?.primaryText ?? section.primaryText,
            cards: nextContent[section.type]?.cards ?? section.cards,
          })),
        }])));
      }
      if (!event.key || event.key === 'cloudsetu_admin_settings') {
        setShowRightEditorInBuilder(readBuilderVisibility());
      }
      if (!event.key || event.key === 'cloudsetu_admin_global_settings') {
        const settings = readStoredAdminSettings();
        setAllowCustomCode(Boolean(settings.allowCustomCode));
        setAllowCustomDomain(settings.allowCustomDomain !== false);
        setBadgeMode(settings.badgeMode || 'Optional');
        if (Array.isArray(settings.starterSectionTypes)) setStarterSectionTypes(settings.starterSectionTypes);
      }
    };
    const savedSettings = readStoredAdminSettings();
    setAllowCustomCode(Boolean(savedSettings.allowCustomCode));
    setAllowCustomDomain(savedSettings.allowCustomDomain !== false);
    setBadgeMode(savedSettings.badgeMode || 'Optional');
    if (Array.isArray(savedSettings.starterSectionTypes)) {
      setStarterSectionTypes(savedSettings.starterSectionTypes);
    }
    window.addEventListener('storage', refreshAdminContent);
    window.addEventListener('cloudsetu-shared-content-updated', refreshAdminContent);
    window.addEventListener('cloudsetu-admin-settings-updated', refreshAdminContent);
    const refreshPartners = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('cloudsetu_partner_pages') || '[]');
        if (stored.length) setPartners([...stored, ...INITIAL_PARTNERS.filter(initial =>
          !stored.some(partner => partner.name === initial.name)
        )]);
      } catch { }
    };
    refreshPartners();
    window.addEventListener('storage', refreshPartners);
    window.addEventListener('cloudsetu-page-builder-updated', refreshPartners);
    const loadPartners = async () => {
      try {
        const response = await axios.get(`${API}/white-label/admin/partners`);
        const serverPartners = response.data?.partners || response.data || [];
        if (Array.isArray(serverPartners) && serverPartners.length) {
          setPartners(previous => [...serverPartners, ...previous.filter(partner =>
            !serverPartners.some(serverPartner => serverPartner.id === partner.id || serverPartner.name === partner.name)
          )]);
        }
      } catch { }
    };
    loadPartners();
    return () => {
      window.removeEventListener('storage', refreshAdminContent);
      window.removeEventListener('cloudsetu-shared-content-updated', refreshAdminContent);
      window.removeEventListener('cloudsetu-admin-settings-updated', refreshAdminContent);
      window.removeEventListener('storage', refreshPartners);
      window.removeEventListener('cloudsetu-page-builder-updated', refreshPartners);
    };
  }, []);

  const filteredPartners = partners.filter(partner =>
    partner.name.toLowerCase().includes(search.toLowerCase()) &&
    (statusFilter === 'All' || partner.status === statusFilter)
  );

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">White Label Manager</h1>
          <p className="text-sm text-slate-500 mt-1">Manage section access, partner pages, and storefront defaults.</p>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-full sm:w-fit overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${activeTab === tab.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                  }`}
              >
                <Icon className="h-4 w-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'library' && (
          <>
          <Card>
            <CardHeader className="border-b border-slate-100 flex-row items-center justify-between">
              <CardTitle className="text-base">Sections Library</CardTitle>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setAddingSection(value => !value);
                }}
              ><Plus className="h-4 w-4 mr-1.5" /> Restore Section</Button>
            </CardHeader>
            {librarySaveStatus && (
              <p role="status" className={`border-b px-4 py-2 text-xs ${librarySaveStatus.startsWith('Could not')
                ? 'border-red-100 bg-red-50 text-red-700'
                : 'border-emerald-100 bg-emerald-50 text-emerald-700'}`}>
                {librarySaveStatus}
              </p>
            )}
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="w-14 px-3 py-3 text-left font-medium">Designs</th>
                    <th className="w-24 px-4 py-3 text-left font-medium">Order</th>
                    <th className="px-4 py-3 text-left font-medium">Section / Variants</th>
                    <th className="w-28 px-4 py-3 text-left font-medium">Status</th>
                    <th className="w-28 px-4 py-3 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section, index) => (
                    <Fragment key={section.type}>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          onClick={() => setExpandedVariantsType(expandedVariantsType === section.type ? null : section.type)}
                          aria-expanded={expandedVariantsType === section.type}
                          aria-controls={`section-variants-${section.type}`}
                          aria-label={`${expandedVariantsType === section.type ? 'Hide' : 'Show'} ${section.name} variant designs`}
                        >
                          {expandedVariantsType === section.type
                            ? <ChevronUp className="h-4 w-4" aria-hidden="true" />
                            : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                        </button>
                      </td>
                      <td className="px-4 py-3"><div className="flex items-center gap-1 text-slate-400"><GripVertical className="h-4 w-4" />{index + 1}</div></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-14 shrink-0 rounded bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center">
                            <LayoutTemplate className="h-4 w-4 text-blue-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800">{section.name}</p>
                            <p className="text-xs text-slate-400">
                              {section.enabled && !section.deleted
                                ? (Array.isArray(section.variants) ? section.variants : []).filter(variant => !(section.disabledVariants || []).includes(variant)).length
                                : 0} available variants
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => persistSections(sections.map((item, itemIndex) => {
                            if (itemIndex !== index) return item;
                            if (item.deleted) return { ...item, deleted: false, enabled: true };
                            return { ...item, enabled: !item.enabled };
                          }))}
                          aria-label={`${section.enabled && !section.deleted ? 'Disable' : 'Enable'} ${section.name} for resellers`}
                          aria-pressed={section.enabled && !section.deleted}
                          title={`${section.enabled && !section.deleted ? 'Enabled' : 'Disabled'} for resellers`}
                          className={`relative w-10 h-5 rounded-full transition-colors ${section.enabled && !section.deleted ? 'bg-blue-600' : 'bg-slate-300'}`}
                        >
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${section.enabled && !section.deleted ? 'left-5' : 'left-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setExpandedVariantsType(section.type);
                              setEditingVariantsType(editingVariantsType === section.type ? null : section.type);
                            }}
                            title="Edit variants"
                          ><Pencil className="h-4 w-4" /></Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (!window.confirm(`Delete ${section.name} from the page builder library?`)) return;
                              persistSections(sections.map(item => item.type === section.type
                                ? { ...item, enabled: false, deleted: true }
                                : item));
                            }}
                            title="Delete section"
                          ><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                    {expandedVariantsType === section.type && (
                      <tr className="border-t border-slate-100 bg-slate-50/70">
                        <td colSpan={5} className="px-6 py-4">
                          <div id={`section-variants-${section.type}`} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {(section.enabled && !section.deleted && Array.isArray(section.variants) ? section.variants : [])
                              .filter(variantName => !(section.disabledVariants || []).includes(variantName))
                              .map((variantName, variantIndex) => {
                              const sampleContent = DEFAULT_SHARED_STOREFRONT_CONTENT[section.type] || {};
                              const designPreview = {
                                ...section,
                                ...sampleContent,
                                variant: variantName,
                              };
                              return (
                                <article
                                  key={`${variantName}-${variantIndex}`}
                                  className="overflow-hidden rounded-lg border border-slate-200 bg-transparent shadow-sm"
                                  data-testid={`admin-variant-design-${section.type}-${variantIndex + 1}`}
                                >
                                  <div className="h-32 overflow-hidden bg-transparent">
                                    <div className="pointer-events-none h-[440px] w-[400%] origin-top-left scale-[0.25]">
                                      <StorefrontSection section={designPreview} />
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-blue-50 text-[10px] font-bold text-blue-700">{variantIndex + 1}</span>
                                    <span className="truncate text-xs font-medium text-slate-700" title={variantName}>{variantName}</span>
                                  </div>
                                </article>
                              );
                            })}
                            {(!section.enabled || !section.variants?.length || section.variants.every(variantName => (section.disabledVariants || []).includes(variantName)) || section.deleted)
                              && <span className="text-xs text-slate-400">No enabled variant designs available.</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {editingVariantsType && (() => {
            const editingSection = sections.find(item => item.type === editingVariantsType && !item.deleted);
            if (!editingSection) return null;
            const names = Array.isArray(editingSection.variants) ? editingSection.variants : [];
            const disabledVariants = Array.isArray(editingSection.disabledVariants) ? editingSection.disabledVariants : [];
            return (
              <div className="fixed inset-0 z-[10000] bg-slate-950/50 flex items-center justify-center p-6" role="dialog" aria-modal="true">
              <Card className="w-[min(720px,calc(100vw-48px))] max-h-[80vh] overflow-auto shadow-2xl">
                <CardHeader className="border-b border-slate-100 flex-row items-center justify-between">
                  <CardTitle className="text-base">{editingSection.name} - Manage Variants</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setEditingVariantsType(null)}>Close</Button>
                </CardHeader>
                <CardContent className="space-y-2 p-4">
                  {names.map((variantName, variantIndex) => (
                    <div key={`${variantName}-${variantIndex}`} className="grid grid-cols-[28px_34px_minmax(180px,1fr)_auto] items-center gap-2 border-b border-slate-100 pb-2">
                      <input
                        type="checkbox"
                        checked={!disabledVariants.includes(variantName)}
                        onChange={event => {
                          if (!event.target.checked && names.filter(name => !disabledVariants.includes(name)).length <= 1) {
                            event.target.checked = true;
                            window.alert('Keep at least one variant enabled for this section.');
                            return;
                          }
                          const nextDisabled = event.target.checked
                            ? disabledVariants.filter(name => name !== variantName)
                            : [...new Set([...disabledVariants, variantName])];
                          persistSections(sections.map(item => item.type === editingVariantsType ? { ...item, disabledVariants: nextDisabled } : item));
                        }}
                        className="h-4 w-4 accent-blue-600"
                        aria-label={`Enable ${variantName}`}
                      />
                      <span className="text-xs text-slate-400">{variantIndex + 1}</span>
                      <Input
                        defaultValue={variantName}
                        onBlur={event => {
                          const nextName = event.target.value.trim();
                          if (!nextName || nextName === variantName || names.includes(nextName)) {
                            event.target.value = variantName;
                            return;
                          }
                          const nextNames = [...names];
                          nextNames[variantIndex] = nextName;
                          persistSections(sections.map(item => item.type === editingVariantsType ? {
                            ...item,
                            variants: nextNames,
                            disabledVariants: disabledVariants.map(name => name === variantName ? nextName : name)
                          } : item));
                        }}
                        className="h-9"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={names.length <= 1}
                        onClick={() => {
                          if (names.length <= 1) return;
                          if (!disabledVariants.includes(variantName)
                            && names.filter(name => !disabledVariants.includes(name)).length <= 1) {
                            window.alert('Keep at least one variant enabled for this section.');
                            return;
                          }
                          persistSections(sections.map(item => item.type === editingVariantsType ? {
                            ...item,
                            variants: names.filter((_, index) => index !== variantIndex),
                            disabledVariants: disabledVariants.filter(name => name !== variantName)
                          } : item));
                        }}
                      ><Trash2 className="h-4 w-4 mr-1 text-red-500" /> Delete</Button>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <Input placeholder="New variant name" className="h-9" onKeyDown={event => {
                      if (event.key !== 'Enter') return;
                      const nextName = event.currentTarget.value.trim();
                      if (!nextName || names.includes(nextName)) return;
                      persistSections(sections.map(item => item.type === editingVariantsType ? { ...item, variants: [...names, nextName] } : item));
                      event.currentTarget.value = '';
                    }} />
                    <Button size="sm" onClick={event => {
                      const input = event.currentTarget.parentElement?.querySelector('input');
                      const nextName = input?.value.trim();
                      if (!nextName || names.includes(nextName)) return;
                      persistSections(sections.map(item => item.type === editingVariantsType ? { ...item, variants: [...names, nextName] } : item));
                      if (input) input.value = '';
                    }}><Plus className="h-4 w-4 mr-1" /> Add Variant</Button>
                  </div>
                </CardContent>
              </Card>
              </div>
            );
          })()}
          {addingSection && (
            <div className="fixed inset-0 z-[10000] bg-slate-950/50 flex items-center justify-center p-6" role="dialog" aria-modal="true">
            <Card className="w-[min(720px,calc(100vw-48px))] max-h-[80vh] overflow-auto shadow-2xl">
              <CardHeader className="border-b border-slate-100 flex-row items-center justify-between">
                <CardTitle className="text-base">Restore a Built-in Section</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setAddingSection(false)}>Close</Button>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                <p className="text-sm text-slate-500">
                  Restore disabled sections here. To add a section to a site, open Page Builder and use the + button beside its design.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {sections.filter(item => item.deleted || !item.enabled).map(item => (
                  <Button
                    key={item.type}
                    type="button"
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => {
                      persistSections(sections.map(entry => entry.type === item.type ? { ...entry, deleted: false, enabled: true } : entry));
                      setAddingSection(false);
                    }}
                  >
                    <LayoutTemplate className="h-4 w-4 mr-2 shrink-0" />
                    <span className="text-left"><span className="block font-semibold">{item.name}</span><span className="block text-xs text-slate-400">{item.variants.length} variants</span></span>
                  </Button>
                ))}
                {!sections.some(item => item.deleted || !item.enabled) && (
                  <p className="text-sm text-slate-500">
                    All built-in sections are active. Open Page Builder to add sections or variants to a site.
                  </p>
                )}
                </div>
                <Button type="button" onClick={() => { setAddingSection(false); setActiveTab('builder'); }}>
                  Open Page Builder
                </Button>
              </CardContent>
            </Card>
            </div>
          )}
          </>
        )}

        {activeTab === 'builder' && (() => {
          const builderSections = adminTemplatePages[adminTemplatePage]?.sections || [];
          const templateEntries = Object.entries(adminTemplatePages)
            .sort(([left], [right]) => Number(left) - Number(right));
          const currentTemplate = adminTemplatePages[adminTemplatePage];
          const selectedBuilderSection = builderSections.find(section => section.id === adminSelectedSectionId) || builderSections[0];
          const selectedBlockId = adminSelectedElement.startsWith('block:') ? adminSelectedElement.slice(6) : null;
          const selectedBlock = selectedBuilderSection?.blocks?.find(block => block.id === selectedBlockId);
          const sectionContent = section => ({
            ...section,
            ...sharedContent[section.type],
          });
          const selectedContent = selectedBuilderSection ? sectionContent(selectedBuilderSection) : null;
          const selectedCardIndex = adminSelectedElement.startsWith('card-') ? Number(adminSelectedElement.slice(5)) : -1;
          const selectedCard = selectedCardIndex >= 0 ? selectedContent?.cards?.[selectedCardIndex] : null;
          const builderVariants = selectedBuilderSection
            ? sections.find(item => item.type === selectedBuilderSection.type)?.variants || []
            : [];
          const styleTargets = selectedBuilderSection
            ? [
                ['section', 'Section'],
                ['heading', 'Heading'],
                ['description', 'Description'],
                ['button', 'Button'],
                ['cards', 'Cards layout'],
                ...(selectedBuilderSection.type === 'hero' ? [['media', 'Hero media']] : []),
                ...(selectedBuilderSection.contentOverrides?.cards || selectedBuilderSection.cards || sharedContent[selectedBuilderSection.type]?.cards || []).map((_, index) => [`card-${index}`, `Card ${index + 1}`]),
                ...(selectedBuilderSection.blocks || []).map(block => [`block:${block.id}`, `Block: ${block.title || block.type}`]),
              ]
            : [];
          const selectedStyle = selectedBuilderSection
            ? getSectionStylesForBreakpoint(selectedBuilderSection, adminPreviewWidth)[adminSelectedElement] || {}
            : {};
          const hasBreakpointOverrides = Boolean(selectedBuilderSection?.responsiveStyles?.[adminPreviewWidth]);
          const styleNumber = property => Number.parseFloat(selectedStyle[property]) || 0;
          return (
            <div className="space-y-5" data-testid="admin-page-builder">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Admin Page Builder</h2>
                  <p className="mt-1 text-sm text-slate-500">Create and manage storefront designs here. Every design uses the same section content; layouts and variants can differ.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" onClick={addAdminTemplatePage} data-testid="admin-add-template">Add design</Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={saveAdminBuilderToBackend}
                    disabled={adminBuilderSaving}
                    data-testid="admin-save-design"
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <Save className="mr-1 h-3.5 w-3.5" /> {adminBuilderSaving ? 'Saving…' : 'Save changes'}
                  </Button>
                  {settingsSaveStatus && (
                    <span className={`text-xs font-medium ${settingsSaveStatus.includes('saved') ? 'text-green-700' : 'text-red-600'}`}>
                      {settingsSaveStatus}
                    </span>
                  )}
                  <Button type="button" size="sm" variant="outline" disabled={!adminPageHistory.past.length} onClick={undoAdminBuilderChange} aria-label="Undo builder change">Undo</Button>
                  <Button type="button" size="sm" variant="outline" disabled={!adminPageHistory.future.length} onClick={redoAdminBuilderChange} aria-label="Redo builder change">Redo</Button>
                  <label className="sr-only" htmlFor="admin-preview-width">Responsive preview and edit breakpoint</label>
                  <select id="admin-preview-width" aria-label="Responsive preview and edit breakpoint" value={adminPreviewWidth} onChange={event => {
                    setAdminPreviewWidth(event.target.value);
                  }} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs">
                    <option value="desktop">Desktop preview</option>
                    <option value="tablet">Tablet preview</option>
                    <option value="mobile">Mobile preview</option>
                  </select>
                  <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    Change site design
                    <select
                      aria-label="Change site design"
                      value={adminTemplatePage}
                      onChange={event => changeAdminBuilderPage(Number(event.target.value))}
                      className="h-9 min-w-[210px] rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800"
                      data-testid="admin-builder-template-select"
                    >
                      {templateEntries.map(([pageIndex, page], order) => <option key={pageIndex} value={pageIndex}>Site {order + 1} — {page.name || `Site ${Number(pageIndex) + 1}`}</option>)}
                    </select>
                  </label>
                  <Button type="button" size="sm" variant="outline" onClick={deleteAdminTemplatePage} disabled={templateEntries.length <= 1} aria-label="Delete current design">
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete design
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="admin-template-name" className="text-xs font-semibold text-slate-600">Design name</label>
                <Input
                  id="admin-template-name"
                  key={`${adminTemplatePage}-${currentTemplate?.name || ''}`}
                  defaultValue={currentTemplate?.name || `Site ${adminTemplatePage + 1}`}
                  onBlur={event => renameAdminTemplatePage(adminTemplatePage, event.target.value)}
                  className="h-9 max-w-sm"
                  maxLength={60}
                />
              </div>

              <div
                className="grid min-w-0 grid-cols-1 items-stretch gap-0 xl:h-[calc(100vh-280px)] xl:min-h-[360px] xl:max-h-[720px] xl:grid-cols-[var(--admin-builder-columns)]"
                style={{
                  '--admin-builder-columns': showRightEditorInBuilder
                    ? `${adminLibraryPanelWidth}px 10px minmax(0,1fr) 10px ${adminPropertiesPanelWidth}px`
                    : `${adminLibraryPanelWidth}px 10px minmax(0,1fr)`,
                }}
              >
                <Card className="flex flex-col overflow-hidden xl:h-full xl:min-h-0">
                  <CardHeader className="border-b border-slate-100 py-3 lg:shrink-0">
                    <CardTitle className="text-sm">Add sections to this site</CardTitle>
                    <p className="text-xs text-slate-500">Choose a design and press +. You can add the same section more than once.</p>
                  </CardHeader>
                  <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                    {sections.filter(section => section.enabled && !section.deleted).map(section => (
                      <div key={section.type} className="rounded-lg border border-slate-200 p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-slate-800">{section.name}</span>
                          <span className="text-[10px] text-slate-400">{section.variants.length} variants</span>
                        </div>
                        <div className="space-y-1">
                          {section.variants.filter(variant => !(section.disabledVariants || []).includes(variant)).map(variant => (
                            <div key={variant} className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1.5">
                              <span className="truncate text-[11px] text-slate-600">{variant}</span>
                              <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => addAdminBuilderSection(section, variant)} aria-label={`Add ${section.name} — ${variant} to Site ${adminTemplatePage + 1}`} title={`Add ${variant} to this site`}>
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {!sections.some(section => section.enabled && !section.deleted) && <p className="text-xs text-slate-400">Enable sections in the library to add them to the canvas.</p>}
                  </CardContent>
                </Card>

                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize section library and canvas"
                  data-testid="admin-library-canvas-resizer"
                  onPointerDown={event => beginAdminPanelResize(event, 'library')}
                  onMouseDown={event => beginAdminPanelResize(event, 'library', 'mouse')}
                  className="group hidden cursor-col-resize touch-none items-center justify-center hover:bg-blue-50 xl:flex"
                >
                  <span className="h-12 w-1 rounded-full bg-slate-300 group-hover:bg-blue-500" />
                </div>

                <Card className="flex flex-col overflow-hidden xl:h-full xl:min-h-0">
                  <CardHeader className="flex-row items-center justify-between border-b border-slate-100 py-3 lg:shrink-0">
                    <CardTitle className="text-sm">{currentTemplate?.name || `Site ${adminTemplatePage + 1}`}</CardTitle>
                    <span className="text-xs text-slate-400">{builderSections.length} sections</span>
                  </CardHeader>
                  <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-100 p-4">
                    <div
                      className="mx-auto overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                      style={{
                        width: adminPreviewWidth === 'mobile' ? '390px' : adminPreviewWidth === 'tablet' ? '768px' : '100%',
                        maxWidth: '100%',
                      }}
                    >
                      <div className="flex h-12 items-center justify-between border-b px-4">
                        <strong className="text-xs text-slate-800">Storefront preview</strong>
                        <span className="text-[10px] text-slate-400">Admin canvas</span>
                      </div>
                      {builderSections.map((section, index) => (
                        (() => {
                          const content = sectionContent(section);
                          const previewStyles = getSectionStylesForBreakpoint(section, adminPreviewWidth);
                          return (
                        <div
                          key={section.id}
                          onClick={event => {
                            setAdminSelectedSectionId(section.id);
                            setAdminSelectedElement(event.target.closest('[data-admin-element]')?.dataset.adminElement || 'section');
                          }}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              setAdminSelectedSectionId(section.id);
                              setAdminSelectedElement('section');
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          data-testid={`admin-canvas-section-${section.type}`}
                          data-admin-section-type={section.type}
                          className={`relative cursor-pointer border-b border-slate-100 p-5 transition ${selectedBuilderSection?.id === section.id ? 'outline outline-2 outline-blue-500' : ''} ${section.visible ? 'bg-white' : 'bg-slate-100 opacity-60'}`}
                          style={previewStyles.section || {}}
                        >
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600">{section.name} · {section.variant}</span>
                            <div className="flex gap-1" onClick={event => event.stopPropagation()}>
                              <Button type="button" variant="ghost" size="sm" className="!h-7 !w-7 !shrink-0 !px-0 !py-0 [&_svg]:!h-4 [&_svg]:!w-4 [&_svg]:!shrink-0" disabled={index === 0} onClick={() => moveAdminBuilderSection(section.id, -1)} aria-label="Move section up"><ChevronUp /></Button>
                              <Button type="button" variant="ghost" size="sm" className="!h-7 !w-7 !shrink-0 !px-0 !py-0 [&_svg]:!h-4 [&_svg]:!w-4 [&_svg]:!shrink-0" disabled={index === builderSections.length - 1} onClick={() => moveAdminBuilderSection(section.id, 1)} aria-label="Move section down"><ChevronDown /></Button>
                              <Button type="button" variant="ghost" size="sm" className="!h-7 !w-7 !shrink-0 !px-0 !py-0 [&_svg]:!h-4 [&_svg]:!w-4 [&_svg]:!shrink-0" onClick={() => updateAdminBuilderSection(section.id, { visible: !section.visible })} aria-label={section.visible ? 'Hide section' : 'Show section'}>{section.visible ? <Eye /> : <EyeOff />}</Button>
                              <Button type="button" variant="ghost" size="sm" className="!h-7 !w-7 !shrink-0 !px-0 !py-0 text-red-600 [&_svg]:!h-4 [&_svg]:!w-4 [&_svg]:!shrink-0" onClick={() => {
                                const nextSections = builderSections.filter(item => item.id !== section.id);
                                persistAdminBuilderPages({
                                  ...adminTemplatePages,
                                  [adminTemplatePage]: { ...adminTemplatePages[adminTemplatePage], sections: nextSections },
                                });
                                if (adminSelectedSectionId === section.id) setAdminSelectedSectionId(nextSections[0]?.id || null);
                              }} aria-label="Remove section"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          {section.type === 'header' && (
                            <StorefrontSection section={{ ...section, ...content, brandName: 'Your brand', styles: previewStyles }} />
                          )}
                          <h3 data-admin-element="heading" className="text-lg font-bold text-slate-900" style={previewStyles.heading || {}}>{content.heading}</h3>
                          <div data-admin-element="description" className="mt-1 text-sm text-slate-600" style={previewStyles.description || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.subtext) }} />
                          {content.cards?.length > 0 && (
                            <div data-admin-element="cards" className="mt-4 grid gap-2 sm:grid-cols-3" style={previewStyles.cards || {}}>
                              {content.cards.slice(0, 3).map((card, cardIndex) => (
                                <div key={`${section.id}-${cardIndex}`} data-admin-element={`card-${cardIndex}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3" style={previewStyles[`card-${cardIndex}`] || {}}>
                                  {card.image && <img src={normalizeSafeUrl(card.image)} alt={card.imageAlt || card.title || ''} className="mb-2 max-h-24 w-full rounded object-cover" />}
                                  <span className="text-lg">{card.icon}</span>
                                  <p className="mt-1 text-xs font-semibold text-slate-800">{card.title || card.question || card.category}</p>
                                  <p className="mt-1 line-clamp-2 text-[10px] text-slate-500">{card.text || card.answer || card.summary || card.price}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {content.primaryText && <span data-admin-element="button" className="mt-3 inline-block rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white" style={previewStyles.button || {}}>{content.primaryText}</span>}
                          {content.mediaUrl && section.type === 'hero' && <img data-admin-element="media" src={normalizeSafeUrl(content.mediaUrl)} alt={content.mediaAlt || ''} className="mt-3 max-h-40 w-full rounded-lg object-contain object-left" style={previewStyles.media || {}} />}
                          {(section.blocks || []).map(block => {
                            const blockStyle = previewStyles[`block:${block.id}`] || {};
                            const commonProps = {
                              'data-admin-element': `block:${block.id}`,
                              className: 'mt-3',
                              style: blockStyle,
                              draggable: true,
                              onDragStart: event => event.dataTransfer.setData('text/admin-builder-block', block.id),
                              onDragOver: event => event.preventDefault(),
                              onDrop: event => {
                                event.preventDefault();
                                reorderAdminBuilderBlocks(section.id, event.dataTransfer.getData('text/admin-builder-block'), block.id);
                              },
                            };
                            if (block.type === 'heading') return <h4 key={block.id} {...commonProps} className={`${commonProps.className} font-bold`}>{block.title}</h4>;
                            if (block.type === 'button') return <button key={block.id} {...commonProps} type="button" title={`Link: ${block.href || '#contact'}`} className={`${commonProps.className} rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white`}>{block.title}</button>;
                            if (block.type === 'card' || block.type === 'container') return <div key={block.id} {...commonProps} className={`${commonProps.className} rounded-lg border border-slate-200 bg-white p-3`}><strong>{block.title}</strong><p className="mt-1 text-xs">{block.text}</p>{(block.children || []).map(child => child.type === 'heading' ? <h5 key={child.id} className="mt-2 font-semibold">{child.text}</h5> : <p key={child.id} className="mt-2 text-xs">{child.text}</p>)}</div>;
                            if (block.type === 'shape') return <div key={block.id} {...commonProps} aria-label="Decorative shape" className={`${commonProps.className} h-12 w-20 rounded-md bg-blue-500`} />;
                            return <p key={block.id} {...commonProps} className={`${commonProps.className} whitespace-pre-line`}>{block.title}</p>;
                          })}
                          {selectedBuilderSection?.id === section.id && (
                            <button
                              type="button"
                              aria-label="Drag to resize section"
                              title="Drag to resize section"
                              onPointerDown={event => beginAdminSectionResize(event, section)}
                              className="absolute bottom-1 right-1 flex h-6 w-6 cursor-nwse-resize touch-none items-center justify-center rounded border-2 border-blue-600 bg-white text-blue-700 shadow-sm hover:bg-blue-50"
                            >
                              <MoveDiagonal className="h-3.5 w-3.5 shrink-0" />
                            </button>
                          )}
                        </div>
                          );
                        })()
                      ))}
                      {!builderSections.length && <div className="p-12 text-center text-sm text-slate-400">Add a section from the library to start this site.</div>}
                    </div>
                  </CardContent>
                </Card>

                {showRightEditorInBuilder && (
                  <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize canvas and properties editor"
                    data-testid="admin-canvas-properties-resizer"
                    onPointerDown={event => beginAdminPanelResize(event, 'properties')}
                    onMouseDown={event => beginAdminPanelResize(event, 'properties', 'mouse')}
                    className="group hidden cursor-col-resize touch-none items-center justify-center hover:bg-blue-50 xl:flex"
                  >
                    <span className="h-12 w-1 rounded-full bg-slate-300 group-hover:bg-blue-500" />
                  </div>
                )}

                {showRightEditorInBuilder && <Card className="flex flex-col overflow-hidden xl:h-full xl:min-h-0" data-testid="admin-properties-editor">
                  <CardHeader className="border-b border-slate-100 py-3 lg:shrink-0"><CardTitle className="text-sm">{selectedBuilderSection ? `${selectedBuilderSection.name} Editor` : 'Properties Editor'}</CardTitle></CardHeader>
                  {selectedBuilderSection ? (
                    <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
                      <label className="block space-y-1 text-xs font-medium text-slate-600">
                        Variant
                        <select
                          value={selectedBuilderSection.variant}
                          onChange={event => updateAdminBuilderSection(selectedBuilderSection.id, { variant: event.target.value })}
                          className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                          data-testid="admin-builder-variant"
                        >
                          {builderVariants.filter(variant => !(sections.find(item => item.type === selectedBuilderSection.type)?.disabledVariants || []).includes(variant)).map(variant => <option key={variant}>{variant}</option>)}
                        </select>
                      </label>
                      <label className="block space-y-1 text-xs font-medium text-slate-600">
                        Page heading
                        <Input value={selectedContent?.heading || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'heading', event.target.value)} data-testid="admin-builder-heading" />
                      </label>
                      <RichTextEditor
                        label="Page description"
                        value={selectedContent?.subtext || ''}
                        onChange={value => updateAdminBuilderContent(selectedBuilderSection.id, 'subtext', value)}
                        placeholder="Write detailed section content..."
                        minHeight="160px"
                        className="w-full min-w-0 text-xs"
                      />
                      {['hero', 'cta', 'header'].includes(selectedBuilderSection.type) && (
                        <>
                          {['hero', 'cta'].includes(selectedBuilderSection.type) && <label className="block space-y-1 text-xs font-medium text-slate-600">
                            Button text
                            <Input value={selectedContent?.primaryText || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'primaryText', event.target.value)} />
                          </label>}
                          {['hero', 'cta'].includes(selectedBuilderSection.type) && <label className="block space-y-1 text-xs font-medium text-slate-600">
                            Button link
                            <Input value={selectedContent?.primaryUrl || ''} placeholder="#features or https://example.com" onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'primaryUrl', event.target.value)} />
                          </label>}
                          {['hero', 'cta'].includes(selectedBuilderSection.type) && <label className="block space-y-1 text-xs font-medium text-slate-600">
                            Image URL
                            <Input value={selectedContent?.mediaUrl || ''} placeholder="Paste an image URL" onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'mediaUrl', event.target.value)} />
                          </label>}
                          {selectedBuilderSection.type === 'hero' && (
                            <label className="block space-y-1 text-xs font-medium text-slate-600">
                              Image alt text
                              <Input value={selectedContent?.mediaAlt || ''} placeholder="Describe the banner image" onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'mediaAlt', event.target.value)} />
                            </label>
                          )}
                          {selectedBuilderSection.type === 'hero' && (
                            <>
                              <label className="block space-y-1 text-xs font-medium text-slate-600">
                                Secondary button text
                                <Input value={selectedContent?.secondaryText || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'secondaryText', event.target.value)} />
                              </label>
                              <label className="block space-y-1 text-xs font-medium text-slate-600">
                                Secondary button link
                                <Input value={selectedContent?.secondaryUrl || ''} placeholder="#contact or https://example.com" onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'secondaryUrl', event.target.value)} />
                              </label>
                            </>
                          )}
                          {selectedBuilderSection.type === 'header' && (
                            <div className="space-y-4 rounded-lg border border-slate-200 p-3">
                              <div className="flex items-center justify-between gap-2">
                                <h3 className="text-xs font-semibold text-slate-800">Navigation links</h3>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateAdminBuilderContent(selectedBuilderSection.id, 'navigation', [
                                    ...(selectedContent?.navigation || []),
                                    { label: 'New link', href: '#' },
                                  ])}
                                ><Plus className="mr-1 h-3 w-3" /> Add link</Button>
                              </div>
                              {(selectedContent?.navigation || []).map((item, index) => (
                                <div key={`header-navigation-${index}`} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                                  <Input aria-label={`Navigation link ${index + 1} label`} value={item.label || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'navigation', selectedContent.navigation.map((link, linkIndex) => linkIndex === index ? { ...link, label: event.target.value } : link))} placeholder="Link label" />
                                  <Input aria-label={`Navigation link ${index + 1} URL`} value={item.href || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'navigation', selectedContent.navigation.map((link, linkIndex) => linkIndex === index ? { ...link, href: event.target.value } : link))} placeholder="#section or URL" />
                                  <Button type="button" variant="ghost" size="sm" aria-label={`Remove navigation link ${index + 1}`} onClick={() => updateAdminBuilderContent(selectedBuilderSection.id, 'navigation', selectedContent.navigation.filter((_, linkIndex) => linkIndex !== index))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                                </div>
                              ))}
                              <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                                <h3 className="text-xs font-semibold text-slate-800">Header buttons</h3>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateAdminBuilderContent(selectedBuilderSection.id, 'actions', [
                                    ...(selectedContent?.actions || []),
                                    { label: 'New button', href: '#contact', style: 'primary' },
                                  ])}
                                ><Plus className="mr-1 h-3 w-3" /> Add button</Button>
                              </div>
                              {(selectedContent?.actions || []).map((item, index) => (
                                <div key={`header-action-${index}`} className="grid gap-2 sm:grid-cols-[1fr_1fr_110px_auto]">
                                  <Input aria-label={`Header button ${index + 1} label`} value={item.label || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'actions', selectedContent.actions.map((action, actionIndex) => actionIndex === index ? { ...action, label: event.target.value } : action))} placeholder="Button label" />
                                  <Input aria-label={`Header button ${index + 1} URL`} value={item.href || ''} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'actions', selectedContent.actions.map((action, actionIndex) => actionIndex === index ? { ...action, href: event.target.value } : action))} placeholder="#section or URL" />
                                  <select aria-label={`Header button ${index + 1} style`} value={item.style || 'primary'} onChange={event => updateAdminBuilderContent(selectedBuilderSection.id, 'actions', selectedContent.actions.map((action, actionIndex) => actionIndex === index ? { ...action, style: event.target.value } : action))} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs">
                                    <option value="primary">Primary</option>
                                    <option value="outline">Outline</option>
                                    <option value="text">Text</option>
                                  </select>
                                  <Button type="button" variant="ghost" size="sm" aria-label={`Remove header button ${index + 1}`} onClick={() => updateAdminBuilderContent(selectedBuilderSection.id, 'actions', selectedContent.actions.filter((_, actionIndex) => actionIndex !== index))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                                </div>
                              ))}
                              <p className="text-[11px] text-slate-500">The reseller logo and company name come from backend branding settings.</p>
                            </div>
                          )}
                        </>
                      )}
                      <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-xs font-semibold text-slate-800">Responsive element styling</h3>
                          <select
                            aria-label="Style target"
                            value={adminSelectedElement}
                            onChange={event => setAdminSelectedElement(event.target.value)}
                            className="h-8 min-w-0 max-w-[150px] rounded-md border border-slate-200 bg-white px-2 text-xs"
                          >
                            {styleTargets.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </div>
                        <div className="flex items-center justify-between gap-2 rounded-md bg-blue-50 px-2 py-1.5">
                          <span className="text-[10px] font-medium text-blue-800">
                            {adminPreviewWidth === 'desktop' ? 'Desktop base styles' : `${adminPreviewWidth === 'mobile' ? 'Mobile' : 'Tablet'} overrides · inherits desktop values`}
                          </span>
                          {adminPreviewWidth !== 'desktop' && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              disabled={!hasBreakpointOverrides}
                              onClick={() => resetAdminBuilderBreakpointStyles(selectedBuilderSection.id, adminPreviewWidth)}
                            >
                              Reset overrides
                            </Button>
                          )}
                        </div>
                        {selectedBlock && (
                          <div className="space-y-2 border-b border-slate-100 pb-3">
                            <label className="block space-y-1 text-[11px] text-slate-600">
                              Block text
                              <Input value={selectedBlock.title || ''} onChange={event => updateAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id, { title: event.target.value })} />
                            </label>
                            {selectedBlock.type === 'button' && (
                              <label className="block space-y-1 text-[11px] text-slate-600">
                                Button link
                                <Input
                                  type="text"
                                  value={selectedBlock.href || ''}
                                  placeholder="#contact or https://example.com"
                                  onChange={event => updateAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id, { href: event.target.value })}
                                  data-testid="admin-builder-button-link"
                                />
                              </label>
                            )}
                            {['card', 'container'].includes(selectedBlock.type) && (
                              <div className="block space-y-1 text-[11px] text-slate-600">
                                Supporting text
                                <RichTextEditor
                                  value={selectedBlock.text || ''}
                                  onChange={value => updateAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id, { text: value })}
                                  minHeight="120px"
                                  className="w-full min-w-0"
                                />
                              </div>
                            )}
                            {selectedBlock.type === 'container' && (
                              <div className="space-y-2 rounded-md bg-slate-50 p-2">
                                <span className="text-[11px] font-semibold text-slate-600">Nested content</span>
                                <div className="flex gap-2">
                                  <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => addAdminNestedBlock(selectedBuilderSection.id, selectedBlock.id, 'heading')}>Add heading</Button>
                                  <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => addAdminNestedBlock(selectedBuilderSection.id, selectedBlock.id, 'text')}>Add text</Button>
                                </div>
                                {(selectedBlock.children || []).map(child => (
                                  <label key={child.id} className="block space-y-1 text-[10px] capitalize text-slate-500">
                                    {child.type}
                                    <Input value={child.text} onChange={event => updateAdminNestedBlock(selectedBuilderSection.id, selectedBlock.id, child.id, event.target.value)} />
                                  </label>
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => moveAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id, -1)} aria-label="Move block up"><ChevronUp className="mr-1 h-3 w-3" />Move up</Button>
                              <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => moveAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id, 1)} aria-label="Move block down"><ChevronDown className="mr-1 h-3 w-3" />Move down</Button>
                            </div>
                            <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => duplicateAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id)}>Duplicate block</Button>
                            <Button type="button" size="sm" variant="outline" className="w-full text-red-600" onClick={() => removeAdminBuilderBlock(selectedBuilderSection.id, selectedBlock.id)}>Remove block</Button>
                          </div>
                        )}
                        {selectedCard && (
                          <div className="space-y-2 border-b border-slate-100 pb-3">
                            {Object.entries(selectedCard).filter(([field, value]) => !['image', 'imageAlt', 'icon', 'imageUrl'].includes(field) && typeof value === 'string').map(([field, value]) => (
                              <div key={field} className="block space-y-1 text-[11px] capitalize text-slate-600">
                                <span>{field}</span>
                                {/text|description|answer|summary|quote|features/i.test(field) ? (
                                  <RichTextEditor
                                    value={value}
                                    onChange={nextValue => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, field, nextValue)}
                                    minHeight="120px"
                                    className="w-full min-w-0"
                                  />
                                ) : (
                                  <textarea value={value} onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, field, event.target.value)} rows={1} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs" />
                                )}
                              </div>
                            ))}
                            <label className="block space-y-1 text-[11px] text-slate-600">
                              Card image
                              <div className="space-y-2">
                                <Input
                                  value={selectedCard.image?.startsWith('data:image/') ? 'Uploaded image saved in this browser' : selectedCard.image || ''}
                                  placeholder="https://example.com/card-image.jpg"
                                  onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'image', event.target.value)}
                                  aria-label="Card image URL"
                                />
                                {selectedCard.image && <img src={normalizeSafeUrl(selectedCard.image)} alt={selectedCard.imageAlt || selectedCard.title || 'Card preview'} className="max-h-24 w-full rounded-md border border-slate-200 object-contain" style={{ width: selectedCard.imageWidth ? `${selectedCard.imageWidth}px` : undefined, height: selectedCard.imageHeight ? `${selectedCard.imageHeight}px` : undefined, maxHeight: selectedCard.imageHeight ? 'none' : undefined }} />}
                                <input
                                  type="file"
                                  accept="image/*"
                                  aria-label="Upload card image"
                                  onChange={event => {
                                    handleAdminBuilderCardImageUpload(selectedBuilderSection.id, selectedCardIndex, event.target.files?.[0]);
                                    event.target.value = '';
                                  }}
                                  className="block w-full text-[10px] text-slate-600 file:mr-2 file:rounded-md file:border-0 file:bg-blue-50 file:px-2 file:py-1 file:text-[10px] file:font-semibold file:text-blue-700"
                                />
                                <Input
                                  value={selectedCard.imageAlt || ''}
                                  placeholder="Image alt text (accessibility)"
                                  onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'imageAlt', event.target.value)}
                                  aria-label="Card image alt text"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="space-y-1 text-[10px] text-slate-500">
                                    Image width (px)
                                    <Input type="number" min="20" max="1600" value={selectedCard.imageWidth || ''} onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'imageWidth', event.target.value === '' ? '' : Number(event.target.value))} />
                                  </label>
                                  <label className="space-y-1 text-[10px] text-slate-500">
                                    Image height (px)
                                    <Input type="number" min="20" max="1200" value={selectedCard.imageHeight || ''} onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'imageHeight', event.target.value === '' ? '' : Number(event.target.value))} />
                                  </label>
                                </div>
                                {cardImageUploadError && <p role="alert" className="text-[10px] text-red-600">{cardImageUploadError}</p>}
                              </div>
                            </label>
                            <label className="block space-y-1 text-[11px] text-slate-600">
                              Card link URL
                              <Input
                                value={selectedCard.linkUrl || ''}
                                placeholder="#features or https://example.com"
                                onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'linkUrl', event.target.value)}
                              />
                            </label>
                            {'icon' in selectedCard && (
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="space-y-1 text-[10px] text-slate-500">
                                    Icon width (px)
                                    <Input type="number" min="16" max="500" value={selectedCard.iconWidth || ''} onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'iconWidth', event.target.value === '' ? '' : Number(event.target.value))} />
                                  </label>
                                  <label className="space-y-1 text-[10px] text-slate-500">
                                    Icon height (px)
                                    <Input type="number" min="16" max="500" value={selectedCard.iconHeight || ''} onChange={event => updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'iconHeight', event.target.value === '' ? '' : Number(event.target.value))} />
                                  </label>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  aria-expanded={adminCardEmojiPickerOpen}
                                  onClick={() => setAdminCardEmojiPickerOpen(open => !open)}
                                >
                                  {adminCardEmojiPickerOpen ? 'Hide emoji picker' : 'Choose emoji'}
                                </Button>
                                {adminCardEmojiPickerOpen && (
                                  <div className="grid max-h-52 grid-cols-8 gap-1 overflow-y-auto overscroll-contain rounded-md border border-slate-200 bg-white p-2">
                                    {COMMON_EMOJIS.map(emoji => (
                                      <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => {
                                          updateAdminBuilderCard(selectedBuilderSection.id, selectedCardIndex, 'icon', emoji);
                                          setAdminCardEmojiPickerOpen(false);
                                        }}
                                        aria-label={`Set card icon to ${emoji}`}
                                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-lg leading-none hover:bg-slate-100"
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            ['margin', 'Margin (px)'],
                            ['padding', 'Padding (px)'],
                            ['fontSize', 'Font size (px)'],
                            ['lineHeight', 'Line height'],
                            ['borderRadius', 'Border radius (px)'],
                            ['width', 'Width (px)'],
                            ['maxWidth', 'Max width (px)'],
                            ['minHeight', 'Min height (px)'],
                            ['gap', 'Gap (px)'],
                          ].map(([property, label]) => (
                            <label key={property} className="space-y-1 text-[10px] text-slate-500">
                              {label}
                              <input
                                type="number"
                                min={property === 'lineHeight' ? 0.5 : 0}
                                step={property === 'lineHeight' ? 0.1 : 1}
                                value={styleNumber(property)}
                                onChange={event => {
                                  const value = event.target.value;
                                  updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, property, value === '' ? '' : property === 'lineHeight' ? Number(value) : `${value}px`, adminPreviewWidth);
                                }}
                                className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs text-slate-800"
                              />
                            </label>
                          ))}
                          <label className="space-y-1 text-[10px] text-slate-500">
                            Text alignment
                            <select
                              value={selectedStyle.textAlign || ''}
                              onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'textAlign', event.target.value, adminPreviewWidth)}
                              className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                            >
                              <option value="">Use layout default</option>
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                          </label>
                          <label className="space-y-1 text-[10px] text-slate-500">
                            Display
                            <select
                              value={selectedStyle.display || ''}
                              onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'display', event.target.value, adminPreviewWidth)}
                              className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                            >
                              <option value="">Use layout default</option>
                              <option value="block">Block</option>
                              <option value="flex">Flex</option>
                              <option value="grid">Grid</option>
                              <option value="none">Hidden</option>
                            </select>
                          </label>
                          <label className="col-span-2 space-y-1 text-[10px] text-slate-500">
                            Grid columns
                            <select
                              value={selectedStyle.gridTemplateColumns || ''}
                              onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'gridTemplateColumns', event.target.value, adminPreviewWidth)}
                              className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                            >
                              <option value="">Use layout default</option>
                              <option value="repeat(1, minmax(0, 1fr))">1 column</option>
                              <option value="repeat(2, minmax(0, 1fr))">2 columns</option>
                              <option value="repeat(3, minmax(0, 1fr))">3 columns</option>
                              <option value="repeat(4, minmax(0, 1fr))">4 columns</option>
                            </select>
                          </label>
                          <label className="space-y-1 text-[10px] text-slate-500">
                            Border
                            <input type="text" value={selectedStyle.border || ''} placeholder="1px solid #cbd5e1" onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'border', event.target.value, adminPreviewWidth)} className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs text-slate-800" />
                          </label>
                          <label className="space-y-1 text-[10px] text-slate-500">
                            Shadow
                            <select
                              value={selectedStyle.boxShadow || ''}
                              onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'boxShadow', event.target.value, adminPreviewWidth)}
                              className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                            >
                              <option value="">Use layout default</option>
                              <option value="none">None</option>
                              <option value="0 1px 3px rgba(15, 23, 42, 0.12)">Small</option>
                              <option value="0 8px 20px rgba(15, 23, 42, 0.14)">Medium</option>
                              <option value="0 20px 40px rgba(15, 23, 42, 0.2)">Large</option>
                            </select>
                          </label>
                          {adminSelectedElement === 'section' && (
                            <>
                              <label className="space-y-1 text-[10px] text-slate-500">
                                Scroll animation
                                <select
                                  value={selectedStyle.scrollAnimation || ''}
                                  onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'scrollAnimation', event.target.value, adminPreviewWidth)}
                                  className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                                >
                                  <option value="">None</option>
                                  <option value="fade">Fade in on scroll</option>
                                  <option value="fade-up">Fade and slide up</option>
                                </select>
                              </label>
                              <label className="space-y-1 text-[10px] text-slate-500">
                                Hover animation
                                <select
                                  value={selectedStyle.hoverAnimation || ''}
                                  onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'hoverAnimation', event.target.value, adminPreviewWidth)}
                                  className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                                >
                                  <option value="">None</option>
                                  <option value="lift">Lift</option>
                                  <option value="scale">Scale</option>
                                </select>
                              </label>
                            </>
                          )}
                          {[
                            ['backgroundColor', 'Background'],
                            ['borderColor', 'Section border'],
                            ['color', 'Text color'],
                          ].map(([property, label]) => (
                            <label key={property} className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
                              {label}
                              <input type="color" value={selectedStyle[property] || (property === 'color' ? '#0f172a' : property === 'borderColor' ? '#64748b' : '#ffffff')} onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, property, event.target.value, adminPreviewWidth)} className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5" />
                            </label>
                          ))}
                          <label className="col-span-2 space-y-1 text-[10px] text-slate-500">
                            Font family
                            <select value={selectedStyle.fontFamily || ''} onChange={event => updateAdminBuilderStyle(selectedBuilderSection.id, adminSelectedElement, 'fontFamily', event.target.value, adminPreviewWidth)} className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800">
                              <option value="">Use inherited font</option>
                              <option value="Inter, sans-serif">Inter</option>
                              <option value="Arial, sans-serif">Arial</option>
                              <option value="Georgia, serif">Georgia</option>
                              <option value="'Times New Roman', serif">Times New Roman</option>
                              <option value="'Courier New', monospace">Courier New</option>
                            </select>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs font-semibold text-slate-800">Add content block</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            ['heading', 'Heading'],
                            ['text', 'Text'],
                            ['button', 'Button'],
                            ['card', 'Card'],
                            ['container', 'Container'],
                            ['shape', 'Shape'],
                          ].map(([type, label]) => <Button key={type} type="button" variant="outline" size="sm" onClick={() => addAdminBuilderBlock(selectedBuilderSection.id, type)}><Plus className="mr-1 h-3 w-3" />{label}</Button>)}
                        </div>
                      </div>
                      {selectedBuilderSection.type !== 'hero' && (
                        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold text-slate-800">Section cards</h3>
                            <Button type="button" size="sm" variant="outline" onClick={() => addAdminBuilderCard(selectedBuilderSection)}><Plus className="mr-1 h-3 w-3" /> Add</Button>
                          </div>
                          {(selectedContent?.cards || []).map((card, cardIndex) => (
                            <div key={`${selectedBuilderSection.id}-editor-card-${cardIndex}`} className="flex items-center justify-between gap-2 rounded bg-slate-50 p-2">
                              <button type="button" className="min-w-0 flex-1 truncate text-left text-xs" onClick={() => setAdminSelectedElement(`card-${cardIndex}`)}>{card.title || card.question || card.category || `Card ${cardIndex + 1}`}</button>
                              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" aria-label={`Remove card ${cardIndex + 1}`} onClick={() => removeAdminBuilderCard(selectedBuilderSection, cardIndex)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                        <span className="text-xs font-medium text-slate-700">Visible on storefront</span>
                        <input type="checkbox" checked={selectedBuilderSection.visible} onChange={event => updateAdminBuilderSection(selectedBuilderSection.id, { visible: event.target.checked })} className="h-4 w-4 accent-blue-600" />
                      </label>
                      <p className="text-[11px] text-slate-400">Page content and layout save in this browser. Shared content remains unchanged.</p>
                    </CardContent>
                  ) : <CardContent><p className="text-xs text-slate-400">Select a canvas section to edit it.</p></CardContent>}
                </Card>}
              </div>
            </div>
          );
        })()}

        {activeTab === 'content' && (
          <div className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Shared Storefront Content</CardTitle>
                <p className="text-sm text-slate-500">Edits here update the same section content across every Page Builder template.</p>
              </CardHeader>
            </Card>
            {Object.entries(sharedContent).map(([type, content]) => {
              const section = INITIAL_SECTIONS.find(item => item.type === type);
              return (
                <Card key={type}>
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="text-base">{section?.name || type}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="space-y-1 text-xs font-medium text-slate-600">
                        Heading
                        <Input value={content.heading} onChange={event => updateSharedSection(type, { heading: event.target.value })} />
                      </label>
                      <label className="space-y-1 text-xs font-medium text-slate-600">
                        Button text
                        <Input value={content.primaryText} onChange={event => updateSharedSection(type, { primaryText: event.target.value })} />
                      </label>
                    </div>
                    <label className="block space-y-1 text-xs font-medium text-slate-600">
                      Description
                      <textarea
                        value={content.subtext}
                        onChange={event => updateSharedSection(type, { subtext: event.target.value })}
                        rows={3}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800 focus:border-blue-500 focus:outline-none"
                      />
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-800">Content items</h3>
                        {!['hero', 'cta'].includes(type) && <Button size="sm" variant="outline" onClick={() => addSharedCard(type)}><Plus className="mr-1 h-4 w-4" /> Add item</Button>}
                      </div>
                      {content.cards.map((card, cardIndex) => (
                        <div key={`${type}-card-${cardIndex}`} className="space-y-3 rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500">Item {cardIndex + 1}</span>
                            <Button size="sm" variant="ghost" onClick={() => updateSharedSection(type, {
                              cards: content.cards.filter((_, index) => index !== cardIndex),
                            })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            {Object.entries(card).map(([field, value]) => (
                              <label key={field} className="space-y-1 text-xs font-medium capitalize text-slate-600">
                                {field.replace(/([A-Z])/g, ' $1')}
                                {typeof value === 'boolean' ? (
                                  <input
                                    type="checkbox"
                                    checked={value}
                                    onChange={event => updateSharedCard(type, cardIndex, field, event.target.checked)}
                                    className="ml-2 h-4 w-4 accent-blue-600"
                                  />
                                ) : Array.isArray(value) ? (
                                  <textarea
                                    value={value.join('\n')}
                                    onChange={event => updateSharedCard(type, cardIndex, field, event.target.value.split('\n'))}
                                    rows={3}
                                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800"
                                  />
                                ) : (
                                  <Input
                                    value={value ?? ''}
                                    onChange={event => updateSharedCard(type, cardIndex, field, event.target.value)}
                                  />
                                )}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      {!content.cards.length && <p className="text-xs text-slate-400">This section does not currently have content items.</p>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {activeTab === 'pages' && (
          <Card>
            <CardHeader className="border-b border-slate-100">
              <div className="flex flex-col sm:flex-row gap-3 justify-between">
                <CardTitle className="text-base">Partner Pages</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search partners" className="h-9 pl-9 w-full sm:w-56" />
                  </div>
                  <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-xs">
                    {['All', 'Published', 'Draft', 'Not Started'].map(status => <option key={status}>{status}</option>)}
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>{['Partner', 'Storefront URL', 'Status', 'Last Updated', 'Actions'].map(header => <th key={header} className="text-left px-4 py-3 font-medium">{header}</th>)}</tr>
                </thead>
                <tbody>
                  {filteredPartners.map(partner => (
                    <tr key={partner.name} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-800">{partner.name}</td>
                      <td className="px-4 py-3 text-blue-600 text-xs">{partner.url || 'Not configured'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs rounded-full px-2 py-1 ${partner.status === 'Published' ? 'bg-green-100 text-green-700'
                          : partner.status === 'Draft' ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-500'
                          }`}>{partner.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{partner.updated}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!partner.url || partner.status === 'Not Started'}
                            onClick={() => {
                              if (!partner.url) return;
                              const url = partner.url.startsWith('http') ? partner.url : `https://${partner.url}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }}
                          ><Eye className="h-3.5 w-3.5 mr-1" /> Preview</Button>
                          <Button size="sm" variant="outline" onClick={() => {
                            const nextName = window.prompt('Partner name', partner.name);
                            if (!nextName?.trim() || nextName.trim() === partner.name) return;
                            const nextPartners = partners.map(item => item === partner ? { ...item, name: nextName.trim(), updated: new Date().toISOString().slice(0, 10) } : item);
                            setPartners(nextPartners);
                            localStorage.setItem('cloudsetu_partner_pages', JSON.stringify(nextPartners));
                            window.dispatchEvent(new Event('cloudsetu-page-builder-updated'));
                          }}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                          <Button size="sm" variant="ghost" title="Reset partner page" onClick={() => {
                            if (!window.confirm(`Reset ${partner.name} to draft defaults?`)) return;
                            const resetPartner = { ...partner, status: 'Draft', published: false, updated: new Date().toISOString().slice(0, 10) };
                            const nextPartners = partners.map(item => item === partner ? resetPartner : item);
                            setPartners(nextPartners);
                            localStorage.setItem('cloudsetu_partner_pages', JSON.stringify(nextPartners));
                            localStorage.removeItem(`cloudsetu_page_builder_${partner.id || partner.name?.toLowerCase().replace(/\s+/g, '-') || 'partner'}`);
                            window.dispatchEvent(new Event('cloudsetu-page-builder-updated'));
                          }}><RotateCcw className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {activeTab === 'all-sites' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">All Site Templates</h2>
              <p className="mt-1 text-sm text-slate-500">These are the default designs available to all resellers as "Ready-made sites". Resellers pick one, customize their content privately, and publish to their own storefront URL.</p>
            </div>
            {Object.keys(adminTemplatePages).length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Globe className="mx-auto h-10 w-10 text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">No designs created yet. Go to <button type="button" className="text-blue-600 underline" onClick={() => setActiveTab('builder')}>Page Builder</button> to create one.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {Object.entries(adminTemplatePages).map(([pageIndex, page]) => {
                  const heroSection = (page.sections || []).find(s => s.type === 'hero');
                  const sectionTypes = (page.sections || []).map(s => s.type);
                  return (
                    <Card key={pageIndex} className="overflow-hidden hover:shadow-md transition-shadow">
                      {/* Mini preview strip */}
                      <div className="relative h-36 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden select-none pointer-events-none">
                        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 50% 0%, #3b82f6 0%, transparent 70%)' }} />
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4">
                          <div className="text-xs font-semibold tracking-widest uppercase text-blue-300 mb-1">
                            {heroSection?.kicker || 'Preview'}
                          </div>
                          <div className="text-sm font-bold text-center leading-tight line-clamp-2">
                            {heroSection?.heading || page.name || `Design ${Number(pageIndex) + 1}`}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1 text-center line-clamp-1">
                            {heroSection?.subtext || 'Cloud & SaaS Solutions'}
                          </div>
                          <div className="mt-3 flex gap-1 flex-wrap justify-center">
                            {sectionTypes.slice(0, 5).map(type => (
                              <span key={type} className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">{type}</span>
                            ))}
                            {sectionTypes.length > 5 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">+{sectionTypes.length - 5} more</span>}
                          </div>
                        </div>
                        <div className="absolute top-2 left-2">
                          <span className="text-[10px] font-bold text-white bg-blue-600 px-2 py-0.5 rounded">Design {Number(pageIndex) + 1}</span>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div>
                            <h3 className="font-semibold text-slate-800 text-sm">{page.name || `Design ${Number(pageIndex) + 1}`}</h3>
                            <p className="text-xs text-slate-500 mt-0.5">{(page.sections || []).length} sections</p>
                          </div>
                          <span className="text-[10px] px-2 py-1 rounded-full bg-green-50 text-green-700 font-medium border border-green-200">Active</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="flex-1 text-xs"
                            onClick={() => { changeAdminBuilderPage(Number(pageIndex)); setActiveTab('builder'); }}
                          >
                            Edit in Builder
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => navigate(`/storefront/preview?page=${pageIndex}&source=admin`)}
                          >
                            <Eye className="h-3 w-3 mr-1" /> Preview
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                            disabled={Object.keys(adminTemplatePages).length <= 1}
                            title="Delete this design"
                            onClick={() => { if (window.confirm(`Delete "${page.name || `Design ${Number(pageIndex)+1}`}"? This cannot be undone.`)) deleteAdminTemplatePage(Number(pageIndex)); }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {/* Add new design card */}
                <button
                  type="button"
                  onClick={() => { addAdminTemplatePage(); setActiveTab('builder'); }}
                  className="rounded-xl border-2 border-dashed border-slate-200 h-[260px] flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <Plus className="h-8 w-8" />
                  <span className="text-sm font-medium">Add new design</span>
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">New Partner Defaults</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <label className="text-sm font-medium text-slate-700">Default color theme</label>
                  <div className="flex gap-2 mt-2">
                    {['#0f172a', '#064e3b', '#581c87', '#7c2d12', '#134e4a'].map(color => (
                      <button key={color} className="w-9 h-9 rounded-lg border-2 border-white shadow ring-1 ring-slate-200" style={{ backgroundColor: color }} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Starter template sections</label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {STARTER_SECTION_OPTIONS.map(([type, label]) => (
                      <label key={type} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={starterSectionTypes.includes(type)}
                          onChange={event => setStarterSectionTypes(current => event.target.checked
                            ? [...new Set([...current, type])]
                            : current.filter(item => item !== type))}
                          className="accent-blue-600"
                        /> {label}
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Platform Permissions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <SettingToggle
                  label="Show right-side properties editor"
                  description="Controls the Admin's right-side properties editor. Resellers can edit content only; they cannot change page layout or styling."
                  checked={showRightEditorInBuilder}
                  onChange={setShowRightEditorInBuilder}
                />
                <SettingToggle label="Allow custom HTML/CSS" description="Advanced page customization for eligible partners." checked={allowCustomCode} onChange={setAllowCustomCode} />
                <SettingToggle label="Allow custom domains" description="Let partners connect their own domains." checked={allowCustomDomain} onChange={setAllowCustomDomain} />
                <div className="rounded-lg border border-slate-200 px-4 py-3">
                  <label className="text-sm font-medium text-slate-700">CloudSetu badge</label>
                  <select value={badgeMode} onChange={event => setBadgeMode(event.target.value)} className="mt-2 w-full h-9 rounded-md border border-slate-200 px-2 text-xs">
                    <option>Optional</option>
                    <option>Mandatory</option>
                    <option>Hidden</option>
                  </select>
                </div>
                <Button className="w-full" onClick={async () => {
                  const settingsValue = { allowCustomCode, allowCustomDomain, badgeMode, starterSectionTypes };
                  const builderSettings = {
                    ...readBuilderVisibilitySettings(),
                    showRightEditorInBuilder,
                  };
                  localStorage.setItem('cloudsetu_admin_global_settings', JSON.stringify(settingsValue));
                  localStorage.setItem('cloudsetu_admin_settings', JSON.stringify(builderSettings));
                  window.dispatchEvent(new Event('cloudsetu-admin-settings-updated'));
                  setSettingsSaveStatus('Saved in this browser.');
                  try {
                    await axios.put(`${API}/white-label/admin/global-settings`, {
                      ...settingsValue,
                      showRightEditorInBuilder,
                    });
                    setSettingsSaveStatus('Saved and synchronized with the server.');
                  } catch (error) {
                    setSettingsSaveStatus(
                      `Saved in this browser only; server sync failed: ${
                        error.response?.data?.detail || error.message || 'server unavailable'
                      }`
                    );
                  }
                }}><CheckCircle className="h-4 w-4 mr-2" /> Save Global Settings</Button>
                {settingsSaveStatus && <p role="status" className="text-xs text-slate-500">{settingsSaveStatus}</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
